import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Stack, Button, Chip, Typography, Divider } from '@mui/material'

import { api } from '../../../api/api'

/**
 * Overrides `navigator.onLine` and dispatches the matching DOM event, so
 * QA can exercise NetworkDetector/SyncEngine's offline<->online transitions
 * from the UI instead of the browser console (see docs/local-first-eval.md §15).
 */
function overrideNavigatorOnline(value) {
  Object.defineProperty(navigator, 'onLine', { get: () => value, configurable: true })
  window.dispatchEvent(new Event(value ? 'online' : 'offline'))
}

function restoreNavigatorOnline() {
  delete navigator.onLine
  window.dispatchEvent(new Event(navigator.onLine ? 'online' : 'offline'))
}

function ConnectionPanel({ isOnline }) {
  const { t } = useTranslation()
  const [navigatorOnline, setNavigatorOnline] = useState(navigator.onLine)
  const [probe, setProbe] = useState(null)
  const [probing, setProbing] = useState(false)

  useEffect(() => {
    const handler = () => setNavigatorOnline(navigator.onLine)
    window.addEventListener('online', handler)
    window.addEventListener('offline', handler)
    return () => {
      window.removeEventListener('online', handler)
      window.removeEventListener('offline', handler)
    }
  }, [])

  const runHealthProbe = useCallback(async () => {
    setProbing(true)
    const startedAt = performance.now()
    try {
      const { data } = await api.get('/health', { timeout: 4000, skipClientType: true })
      const latencyMs = Math.round(performance.now() - startedAt)
      const servicesHealthy = Object.values(data?.services || {}).every(s => s?.status === 'ok')
      const ok = data?.success === true && data?.status === 'healthy' && servicesHealthy
      setProbe({ ok, latencyMs, timestamp: new Date() })
    } catch (err) {
      const latencyMs = Math.round(performance.now() - startedAt)
      setProbe({ ok: false, latencyMs, timestamp: new Date(), error: err.message })
    } finally {
      setProbing(false)
    }
  }, [])

  const isConsistent = navigatorOnline === isOnline

  return (
    <Stack spacing={3} className="audit-section">
      <div>
        <h3>{t('audit_connection_title')}</h3>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Chip label={`Redux isOnline: ${isOnline}`} color={isOnline ? 'success' : 'error'} />
          <Chip label={`navigator.onLine: ${navigatorOnline}`} color={navigatorOnline ? 'success' : 'error'} />
          <Chip
            label={isConsistent ? t('audit_status_consistent') : t('audit_status_inconsistent')}
            color={isConsistent ? 'success' : 'warning'}
            variant="outlined"
          />
        </Stack>
      </div>

      <Divider />

      <div>
        <Typography variant="subtitle2">{t('audit_connection_simulate_title')}</Typography>
        <Typography className="audit-section-note">{t('audit_connection_simulate_note')}</Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Button variant="outlined" color="error" onClick={() => overrideNavigatorOnline(false)}>
            {t('audit_force_offline')}
          </Button>
          <Button variant="outlined" color="success" onClick={() => overrideNavigatorOnline(true)}>
            {t('audit_force_online')}
          </Button>
          <Button variant="text" onClick={restoreNavigatorOnline}>
            {t('audit_restore_detection')}
          </Button>
        </Stack>
      </div>

      <Divider />

      <div>
        <Typography variant="subtitle2">{t('audit_health_probe_title')}</Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
          <Button variant="contained" onClick={runHealthProbe} disabled={probing}>
            {probing ? t('audit_probing') : t('audit_run_health_probe')}
          </Button>
          {probe && (
            <Chip
              label={probe.ok
                ? `${t('audit_health_ok')} · ${probe.latencyMs}ms`
                : `${t('audit_health_failed')}${probe.error ? `: ${probe.error}` : ''}`}
              color={probe.ok ? 'success' : 'error'}
            />
          )}
        </Stack>
        {probe && (
          <Typography className="audit-section-note" sx={{ mt: 1 }}>
            {t('audit_last_checked')}: {probe.timestamp.toLocaleTimeString()}
          </Typography>
        )}
      </div>
    </Stack>
  )
}

const mapStateToProps = state => ({
  isOnline: state.utilities.isOnline,
})

export default connect(mapStateToProps)(ConnectionPanel)
